import type { SiteBuilderRuntimePageLink } from "./siteBuilderTypes";

export const SITE_BUILDER_PAGE_HREF_TOKEN_PREFIX = "{{page.href:";

const PAGE_HREF_TOKEN_PATTERN = /\{\{page\.href:([^{}]+)\}\}/gu;

export function siteBuilderPageHrefToken(templateKey: string): string {
    return `${SITE_BUILDER_PAGE_HREF_TOKEN_PREFIX}${templateKey.trim()}}}`;
}

function normalizedLanguageCode(value: string | null | undefined): string {
    return value?.trim().toLowerCase().replaceAll("_", "-") ?? "";
}

function pageHref(page: SiteBuilderRuntimePageLink): string {
    const explicitHref = page.href?.trim();

    if (explicitHref) {
        return explicitHref;
    }

    const slug = page.slug.trim().replace(/^\/+|\/+$/gu, "");

    if (page.isHomepage || page.templateKey === "home" || slug === "") {
        return "/";
    }

    return `/${slug}`;
}

/**
 * Resolves a template-owned page by its stable template key. The localized
 * runtime href remains authoritative, so translated and collision-suffixed
 * slugs never have to be copied into a template document.
 */
export function siteBuilderPageHref(
    pages: readonly SiteBuilderRuntimePageLink[],
    templateKey: string,
    languageCode?: string | null,
): string | null {
    const normalizedTemplateKey = templateKey.trim();

    if (normalizedTemplateKey === "") {
        return null;
    }

    const candidates = pages.filter((page) => (
        page.templateKey === normalizedTemplateKey
    ));
    const normalizedLanguage = normalizedLanguageCode(languageCode);
    const localized = normalizedLanguage === ""
        ? null
        : candidates.find((page) => (
            normalizedLanguageCode(page.languageCode) === normalizedLanguage
        )) ?? null;
    const page = localized ?? candidates[0] ?? null;

    return page ? pageHref(page) : null;
}

/**
 * Replaces `{{page.href:<templateKey>}}` tokens in authored endpoints.
 * Missing targets become an inert hash instead of leaking a non-routable
 * token into the rendered page.
 */
export function resolveSiteBuilderRuntimeHref(
    value: string,
    pages: readonly SiteBuilderRuntimePageLink[],
    languageCode?: string | null,
): string {
    return value.replace(PAGE_HREF_TOKEN_PATTERN, (_match, templateKey: string) => (
        siteBuilderPageHref(pages, templateKey, languageCode) ?? "#"
    ));
}
